import React from 'react'
import styled from 'styled-components'
import { useRouter } from 'next/router'
import moment from 'moment'
import DOMPurify from 'isomorphic-dompurify'

import Div from '@jarvis.oddle/core/components/Div'
import Card from '@jarvis.oddle/core/components/Card'
import Form from '@jarvis.oddle/core/components/Form'
import Flex from '@jarvis.oddle/core/components/Flex'
import { H3, H4 } from '@jarvis.oddle/core/components/Typo'
import { Column, Grid, Row } from '@jarvis.oddle/core/components/Grid'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { gutterSize } from '@jarvis.oddle/core/theme/default'

import ResponsiveSection from 'src/components/core/ResponsiveSection'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import OutletDropdown from 'src/components/sections/Reservations/OutletDropdown'
import PaxDropdown from 'src/components/sections/Reservations/PaxDropdown'
import DatetimeDropdown from 'src/components/sections/Reservations/DatetimeDropdown'
import TimeSlotsDropdown from 'src/components/sections/Reservations/TimeSlotsDropdown'
import { getColorString, pixelise } from 'src/utils/style'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectSections } from 'src/redux/customisation'
import modalController from 'src/decorators/ModalController'
import { isEmpty } from 'src/utils/string'

const getGutterSize = (fallback) => (props) =>
  pixelise((props.gutterSize || fallback) / 2)

const StyledResponsiveSection = styled(ResponsiveSection)<{
  minHeight: string
  index: number
}>`
  ${(props) =>
    props.index === 0 || props.index === 1 ? 'margin: 0;' : 'margin: 30px 0;'}
  ${breakpoint('mobile')`
    min-height: 480px;
  `}
  ${breakpoint('tablet')`
    min-height: ${(props) => props.minHeight};
  `}
`

const ResponsiveHeightContainer = styled(Div)<{ hideOutlet: boolean }>`
  position: relative;
  width: 100%;
  top: 0;
  padding: 0 0 20px 0;
  @media (max-width: 720px) {
    padding: ${(props) => (props.hideOutlet ? '55px' : '28px')} 0 50px 0;
  }
`

const EmptyBackgroundImage = styled(Div)<{
  imageLayout: string
  reservationCoverOverlayColour: string
  coverImage: string
}>`
  position: absolute;
  height: ${(props) =>
    props.imageLayout === 'background' ? '480px' : '300px'};
  width: 100%;
  background-color: ${(props) =>
    props.reservationCoverOverlayColour
      ? getColorString(props.reservationCoverOverlayColour)
      : 'rgba(0, 0, 0, 0.3)'};
  background-size: cover;
  top: 0;
  z-index: 1;
`

const BackgroundImage = styled(Div)<{ src: string; imageLayout: string }>`
  position: absolute;
  height: ${(props) =>
    props.imageLayout === 'background' ? '480px' : '300px'};
  width: 100%;
  background-image: url('${(props) => props.src}');
  background-size: cover;
  top: 0;
  background-position: center;
  z-index: 0;
`

const StyledPrimaryText = styled(Div)`
  display: none;
  position: relative;
  text-align: center;
  max-width: 1024px;
  margin: auto;
  z-index: 2;
  padding: 0 40px;
  ${breakpoint('tablet')`
    display: flex;
    flex-flow: row wrap;
    align-content: center;
    min-height: 220px;
  `}
`

const StyledTextHeader = styled(Div)<{ reservationPrimaryTextColour: string }>`
  width: 100%;
  font-size: 44px;
  color: ${(props) => props.reservationPrimaryTextColour || '#fff'};
  padding-top: 30px;
`

const StyledTextSubHeader = styled(Div)<{
  reservationPrimaryTextColour: string
}>`
  position: relative;
  width: 100%;
  justify-content: center;
  font-size: 18px;
  color: ${(props) => props.reservationPrimaryTextColour || '#fff'};
  padding: ${(props) => (props.children ? '10px 0 20px' : '20px 0')};
  display: flex;
`

const StyledTextHeaderMobile = styled(Div)`
  width: 100%;
  display: flex;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 28px;
  color: #000;
  margin: 10px 0 16px 0;
  ${breakpoint('tablet')`
    display: none;
  `}
`

const StyledCard = styled(Card)<{ scaleDown: boolean }>`
  position: relative;
  max-width: 980px;
  justify-content: center;
  font-size: 44px;
  background-color: rgba(255, 255, 255, 0.95);
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  margin: 60px auto 60px;
  width: 80%;
  z-index: 2;
  ${breakpoint('mobile')`
    margin: auto;
  `}
  ${breakpoint('tablet')`
    width: ${(props) => (props.scaleDown ? '50%' : '80%')};
  `}
  ${breakpoint('desktop')`
    margin-top: 0 !important;
    padding-left: 50px !important;
    padding-right: 50px !important;
    width: 80%;
  `}
`

const StyledForm = styled(Form)`
  width: 100%;
`

const StyledSecondaryText = styled(Div)<{
  imageLayout: string
  display: boolean
}>`
  display: ${(props) => (props.display ? 'block' : 'none')};
  position: relative;
  text-align: center;
  max-width: 900px;
  margin: auto;
  padding-top: ${(props) =>
    props.imageLayout === 'background' ? '120px' : '20px'};
  @media (max-width: 1023px) {
    padding-top: 70px;
    padding-left: 40px;
    padding-right: 40px;
  }
`

const StyledSecondaryHeader = styled(H3)`
  width: 100%;
  color: ${(props) => props.theme.colorSectionHeadingsFont};
`

const StyledSecondarySubHeader = styled(H4)`
  width: 100%;
  color: ${(props) => props.theme.colorSectionHeadingsFont};
`

const StyledColumn = styled(Column)`
  ${breakpoint('mobile')`
    padding: 4px 10px;
  `}
  ${breakpoint('tablet')`
    padding: ${getGutterSize(gutterSize.tablet)};
  `}
  ${breakpoint('desktop')`
    padding-bottom: ${getGutterSize(gutterSize.desktop)};
    padding-top: 0;
  `}
  #slot {
    width: 93%;
  }
`

const StyledButtonSection = styled(Flex)`
  width: 100%;
  justify-content: center;
  padding-top: ${getGutterSize(gutterSize.mobile)};
  @media (max-width: 720px) {
    padding: 20px 0 10px;
  }
`

const StyledButton = styled(Form.SubmitButton)`
  width: 15rem;
  @media (max-width: 720px) {
    width: 100%;
  }
`

interface ReservationsSection {
  id: string
  name: string
  type: string
  settings: {
    imageLayout: string
    coverImage: string
    primaryHeaderText: string
    reservationPrimaryTextColour: string
    primarySubheaderText: string
    secondaryHeaderText: string
    secondaryBodyText: string
    reservationCoverOverlayColour: string
  }
  updatedAt: string
}

interface ReservationsProps {
  id: string
  index: number
  passingProps?: Record<string, unknown>
}

const Reservations = ({
  id,
  index,
  passingProps
}: ReservationsProps): JSX.Element => {
  const { t } = React.useContext(TranslationContext)
  const router = useRouter()
  const { locale } = router.query
  const [minHeight, setMinHeight] = React.useState('480px')

  const { settings } = useAppSelector(selectSections)[id] as ReservationsSection
  const reservationsInfo = useAppSelector(
    (state) => state['@reservation'].reservationsInfo.data
  )
  const availableDate = useAppSelector(
    (state) => state['@reservation'].availableDate.data
  )
  const outlets =
    useAppSelector((state) => state['@reservation'].brandInfo?.data?.outlets) ||
    []
  const timeSlots =
    useAppSelector((state) => state['@reservation'].timeslot.data) || []
  const brandInfo = useAppSelector((state) => state['@reservation'].brandInfo)

  React.useEffect(() => {
    let height = '480px'
    if (
      typeof window !== 'undefined' &&
      passingProps?.isReservationPage &&
      passingProps?.onlyHaveOneSection
    ) {
      height = `calc(100vh - ${
        window.document.getElementById('oddle-shop-header').offsetHeight +
        window.document.getElementById('oddle-shop-footer').offsetHeight +
        2
      }px)`
    }
    setMinHeight(height)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const reservationStatus = (() => {
    if (
      ['NO_STORE_SUBSCRIPTION_FOUND', 'NO_STORE_ENABLE_FOUND'].includes(
        brandInfo?.error?.errorCode
      )
    ) {
      return {
        text: t('Online Booking Disabled'),
        disabled: true
      }
    }
    if (brandInfo?.error?.errorCode === 'BRAND_DISABLED') {
      return {
        text: t('Restaurant Disabled'),
        disabled: true
      }
    }
    if ((availableDate || []).length === 0) {
      return {
        text: t('No available dates'),
        disabled: true
      }
    }
    if (timeSlots.length === 0) {
      return {
        text: t('No available timeslots'),
        disabled: true
      }
    }
    if (!outlets?.[0]) {
      return {
        text: t('No available stores'),
        disabled: true
      }
    }
    return {
      text: t('Find a table'),
      disabled: false
    }
  })()

  const {
    imageLayout,
    coverImage,
    primaryHeaderText,
    reservationPrimaryTextColour,
    primarySubheaderText,
    secondaryHeaderText,
    secondaryBodyText,
    reservationCoverOverlayColour
  } = settings || {}

  const onFormSubmit = async (values, { setSubmitting }) => {
    const parsedReserveUrl = new URL(reservationsInfo.url)
    const accessToken = window.localStorage.getItem('accessToken')
    const params = new URLSearchParams({
      date: moment(new Date(values.date)).format('DDMMYYYY'),
      timeslot: moment(values.slot, 'HH:mm:ss').format('HHmm'),
      adult: values.numberOfAdult,
      child: values.numberOfChildren || 0,
      outletId: values.outlet.id,
      utm_source: 'oddle-shop'
    })
    if (accessToken) {
      params.append('token', accessToken)
    }
    modalController.showModal('IframeModal', {
      src: `${parsedReserveUrl.origin}/${locale}${
        parsedReserveUrl.pathname
      }?${params.toString()}`
    })
    setSubmitting(false)
  }

  return (
    <StyledResponsiveSection index={index} minHeight={minHeight}>
      <ResponsiveHeightContainer hideOutlet={outlets.length <= 1}>
        {coverImage && (
          <BackgroundImage src={coverImage} imageLayout={imageLayout} />
        )}
        <EmptyBackgroundImage
          coverImage={coverImage}
          imageLayout={imageLayout}
          reservationCoverOverlayColour={reservationCoverOverlayColour}
        />
        <StyledPrimaryText>
          <StyledTextHeader
            reservationPrimaryTextColour={reservationPrimaryTextColour}
          >
            {primaryHeaderText || t('Make a Reservation')}
          </StyledTextHeader>
          <StyledTextSubHeader
            reservationPrimaryTextColour={reservationPrimaryTextColour}
          >
            {primarySubheaderText}
          </StyledTextSubHeader>
        </StyledPrimaryText>
        <StyledCard scaleDown={outlets.length <= 1}>
          <StyledTextHeaderMobile>
            {primaryHeaderText || t('Make a Reservation')}
          </StyledTextHeaderMobile>
          <Flex flexDirection='row'>
            <StyledForm
              initialValues={{
                numberOfAdult:
                  (outlets?.[0]?.maxPartySize || 0) > 2
                    ? outlets?.[0]?.minPartySize || 2
                    : outlets?.[0]?.maxPartySize || 1,
                numberOfChildren: 0,
                date: availableDate?.[0]?.date
                  ? new Date(availableDate[0].date)
                  : new Date(),
                outlet: outlets?.[0],
                slot: timeSlots?.[0]
              }}
              onSubmit={onFormSubmit}
            >
              <Grid>
                <Row>
                  {outlets.length > 1 && (
                    <StyledColumn
                      sm={12}
                      md={outlets.length > 1 ? 6 : 12}
                      lg={3}
                    >
                      <OutletDropdown />
                    </StyledColumn>
                  )}
                  <StyledColumn
                    sm={12}
                    md={outlets.length > 1 ? 6 : 12}
                    lg={outlets.length > 1 ? 3 : 4}
                  >
                    <PaxDropdown />
                  </StyledColumn>
                  <StyledColumn
                    sm={12}
                    md={outlets.length > 1 ? 6 : 12}
                    lg={outlets.length > 1 ? 3 : 4}
                  >
                    <DatetimeDropdown />
                  </StyledColumn>
                  <StyledColumn
                    sm={12}
                    md={outlets.length > 1 ? 6 : 12}
                    lg={outlets.length > 1 ? 3 : 4}
                  >
                    <TimeSlotsDropdown />
                  </StyledColumn>
                </Row>
              </Grid>
              <StyledButtonSection>
                <StyledButton disabled={reservationStatus.disabled}>
                  {reservationStatus.text}
                </StyledButton>
              </StyledButtonSection>
            </StyledForm>
          </Flex>
        </StyledCard>
        <StyledSecondaryText
          imageLayout={imageLayout}
          display={
            !isEmpty(secondaryHeaderText) ||
            !/<p.*><\/p+>/.test(secondaryBodyText)
          }
        >
          {secondaryHeaderText ? (
            <StyledSecondaryHeader
              bold
              reservationPrimaryTextColour={reservationPrimaryTextColour}
            >
              {t(secondaryHeaderText)}
            </StyledSecondaryHeader>
          ) : null}
          {secondaryBodyText ? (
            <StyledSecondarySubHeader>
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(secondaryBodyText)
                }}
              />
            </StyledSecondarySubHeader>
          ) : null}
        </StyledSecondaryText>
      </ResponsiveHeightContainer>
    </StyledResponsiveSection>
  )
}

export default Reservations
